import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@sara/ui/lib/utils';
import { Button } from '@sara/ui/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@sara/ui/components/popover';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@sara/ui/components/command';

export interface CatalogOption {
  value: string;
  label: string;
  /** Optional trailing hint shown muted (e.g. "v3 · current", "archived"). */
  hint?: string;
}

interface CatalogComboboxProps {
  id?: string;
  value: string;
  options: CatalogOption[];
  onChange: (value: string) => void;
  placeholder: string;
  disabledPlaceholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * A searchable single-select (Popover + Command) for the bulk-reprocess
 * Organization / Template / Version pickers. Mirrors the FilterBar's
 * Popover+Command editor pattern; shows a disabled placeholder for the
 * dependent-field locked state (e.g. "Select an organization first").
 */
export function CatalogCombobox({
  id,
  value,
  options,
  onChange,
  placeholder,
  disabledPlaceholder,
  searchPlaceholder = 'Search…',
  emptyText = 'No matches.',
  disabled = false,
  loading = false,
}: CatalogComboboxProps) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className={cn('truncate', !selected && 'text-muted-foreground')}>
            {selected
              ? selected.label
              : loading
                ? 'Loading…'
                : disabled
                  ? (disabledPlaceholder ?? placeholder)
                  : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" strokeWidth={1.6} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {options.map((opt) => (
              <CommandItem
                key={opt.value}
                value={`${opt.label} ${opt.value}`}
                onSelect={() => {
                  onChange(opt.value === value ? '' : opt.value);
                  setOpen(false);
                }}
                className="flex items-center justify-between gap-2 text-[13px]"
              >
                <span className="flex items-center gap-2 truncate">
                  <Check
                    className={cn('h-3.5 w-3.5 shrink-0', opt.value !== value && 'invisible')}
                    strokeWidth={1.7}
                  />
                  <span className="truncate">{opt.label}</span>
                </span>
                {opt.hint ? (
                  <span className="shrink-0 text-[11.5px] text-muted-foreground">{opt.hint}</span>
                ) : null}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
